import { useContext, useEffect, useMemo, useState } from 'react';

import { CommitteeAppointment, Role } from '@domui-domain/districts';
import { useFetchDistrictOfficers } from '@domui-hooks/useFetchDistrictOfficers';
import { GetDistrictCommitteeRoles } from '@domui-hooks/useGetDistrictCommitteeRoles';
import { extractOfficersData } from '@domui-presenters/web/pages/Districts/Officers/Utils';
import {
  areDOsArraysEqual,
  DistrictContext,
  getFormattedRoles,
} from '@domui-use-cases/districts';
import { getRotaryYear } from '@domui-utils/datetime';

// Types
import { DistrictOfficer } from '@typings/graphql';
import { GetRolesQuery } from '@typings/operations';

export const useSetContextManagedRYs = (
  districtId: string,
  riDistrictId: number | null | undefined
) => {
  const currentRY = Number(getRotaryYear());

  const [currentRYOfficers, setCurrentRYOfficers] = useState<DistrictOfficer[]>(
    []
  );
  const [futureRYOfficers, setFutureRYOfficers] = useState<DistrictOfficer[]>(
    []
  );

  const {
    fetchDistrictOfficers: getCurrentRYOfficers,
    data: currentRYOfficersData,
    loading: currentOfficersLoading,
  } = useFetchDistrictOfficers();

  const {
    fetchDistrictOfficers: getFutureRYOfficers,
    data: futureRYOfficersData,
    loading: futureOfficersLoading,
  } = useFetchDistrictOfficers();

  const {
    data: currentRolesData,
    getDistrictCommitteeRoles: getCurrentRoles,
  } = GetDistrictCommitteeRoles();

  const [
    {
      managedRYs: {
        current: {
          roles: contextCurrentRYRoles,
          officers: contextCurrentRYOfficers,
        },
        future: {
          roles: contextFutureRYRoles,
          officers: contextFutureRYOfficers,
        },
      },
    },
    setContext,
  ] = useContext(DistrictContext);

  const setEntityOnContext = (
    year: 'current' | 'future',
    entity:
      | { key: 'roles'; data: Role[] }
      | { key: 'officers'; data: DistrictOfficer[] }
  ) =>
    setContext((prevState: CommitteeAppointment) => ({
      ...prevState,
      managedRYs: {
        ...prevState.managedRYs,
        [year]: {
          ...prevState.managedRYs[year],
          [entity.key]: entity.data,
        },
      },
    }));

  useEffect(() => {
    getCurrentRoles({ rotaryYear: `${currentRY - 1}` });
  }, [currentRY]);

  const currentRawRoles = currentRolesData?.committeeRoles ?? [];
  const currentMappedRoles: GetRolesQuery = useMemo(
    () => ({
      roles: currentRawRoles.map(
        (role: { roleId: string; role: string; termLength: string }) => ({
          id: role.roleId,
          name: role.role,
          termLength: Number(role.termLength),
          isOfficer: true,
        })
      ),
    }),
    [currentRawRoles]
  );

  const currentRYRoles = getFormattedRoles(currentMappedRoles);

  const futureRawRoles = currentRolesData?.committeeRoles ?? [];
  const futureMappedRoles: GetRolesQuery = useMemo(
    () => ({
      roles: futureRawRoles.map(
        (role: { roleId: string; role: string; termLength: string }) => ({
          id: role.roleId,
          name: role.role,
          termLength: Number(role.termLength),
          isOfficer: true,
        })
      ),
    }),
    [futureRawRoles]
  );

  const futureRYRoles = getFormattedRoles(futureMappedRoles);

  useEffect(() => {
    if (!contextCurrentRYRoles.length && currentRYRoles.length) {
      setEntityOnContext('current', {
        key: 'roles',
        data: currentRYRoles,
      });
    }
  }, [contextCurrentRYRoles, currentRYRoles]);

  useEffect(() => {
    if (!contextFutureRYRoles.length && futureRYRoles.length) {
      setEntityOnContext('future', {
        key: 'roles',
        data: futureRYRoles,
      });
    }
  }, [contextFutureRYRoles, futureRYRoles]);

  useEffect(() => {
    if (riDistrictId) {
      getCurrentRYOfficers(districtId, {
        page: 1,
        pageSize: 250,
        rotaryYear: String(currentRY),
        riDistrictId,
      });

      getFutureRYOfficers(districtId, {
        page: 1,
        pageSize: 100,
        rotaryYear: String(currentRY + 1),
        riDistrictId,
      });
    }
  }, [riDistrictId]);

  useEffect(() => {
    setCurrentRYOfficers(extractOfficersData(currentRYOfficersData));
  }, [currentRYOfficersData]);

  useEffect(() => {
    setFutureRYOfficers(extractOfficersData(futureRYOfficersData));
  }, [futureRYOfficersData]);

  useEffect(() => {
    if (
      currentRYOfficers.length &&
      !areDOsArraysEqual(contextCurrentRYOfficers, currentRYOfficers)
    ) {
      setEntityOnContext('current', {
        key: 'officers',
        data: currentRYOfficers,
      });
    }
  }, [contextCurrentRYOfficers, currentRYOfficers]);

  useEffect(() => {
    if (
      futureRYOfficers.length &&
      !areDOsArraysEqual(contextFutureRYOfficers, futureRYOfficers)
    ) {
      setEntityOnContext('future', { key: 'officers', data: futureRYOfficers });
    }
  }, [contextFutureRYOfficers, futureRYOfficers]);

  return {
    currentOfficersLoading,
    futureOfficersLoading,
  };
};
